import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import OsScroll from '../ui/OsScroll';
import './ColumnDropZone.css';

// One object for every column: OsScroll re-applies options when they change.
const COLUMN_SCROLL = { overflow: { x: 'hidden' } };

/**
 * The cards of one kanban list, or the rows of one list-view section.
 *
 * `scroll` makes it its own scroller, and **only the kanban column passes it**:
 * list view scrolls as a whole inside the board's outer `OsScroll`, and a
 * second scroller nested in that one would swallow the wheel.
 *
 * The scrolling variant uses `OsScroll` (OverlayScrollbars) rather than the
 * native bar it used to have. Three things fall out of that, and all three were
 * asked for:
 *
 * - **The bar shows on hover only** — `autoHide: 'leave'` is already the app
 *   default in `OsScroll`, so the column gains it by joining the library rather
 *   than by a rule of its own.
 * - **Its space is reserved** by padding on the content
 *   (`--task-board-scroll-gutter`), so a column's cards do not shift sideways
 *   the first time it overflows, and nothing is ever hidden under the bar.
 * - It removes one surface from the app's two-scrollbar-systems split, which is
 *   a standing item in `60-state.md`.
 *
 * The old native version reserved its gutter with `scrollbar-gutter: stable`
 * and the comment there was load-bearing: a scrollbar appearing mid-drag
 * changes the column's content rect, and that oscillation feeds @dnd-kit's
 * measure→collision→reorder loop until it throws "Maximum update depth". An
 * overlaid scrollbar cannot cause that at all — it never takes layout space in
 * either state — so the hazard is removed rather than merely mitigated.
 */
export function ColumnDropZone({
  groupId,
  className = 'task-board-column-body',
  scroll = false,
  children,
}) {
  const { setNodeRef } = useDroppable({ id: `col:${groupId}` });
  if (!scroll) {
    return (
      <div ref={setNodeRef} className={className}>
        {children}
      </div>
    );
  }
  return (
    <OsScroll className={className} options={COLUMN_SCROLL}>
      {/* The droppable is the CONTENT node, never the host: OverlayScrollbars
          moves children into its own viewport element, so a ref on the host
          would measure a box the cards do not live in. `min-height: 100%` keeps
          the drop target the full height of the column even when it holds two
          cards — without it, dropping into the empty space below them lands on
          nothing. */}
      <div ref={setNodeRef} className="task-board-column-drop">
        {children}
      </div>
    </OsScroll>
  );
}

/** The "Add list" button doubling as a drop target: dropping a task here
 *  creates a new unnamed list at the end and moves the card into it (handled in
 *  handleDragEnd via the `new-list` over id). Only one add-list button is
 *  mounted at a time (the render is a viewMode ternary), so the single id is
 *  safe across kanban/list views. */
export function AddListDropZone({ id = 'new-list', className, onClick, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`${className}${isOver ? ' task-board-add-column-btn--drop' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
